// @flow

import * as React from 'react';
import BoardSortSelectlist from 'app/common/react/components/profile/ProfileHeader/BoardSortSelectlist';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import throttle from 'lodash/throttle';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Router } from 'app/common/lib/ReactRouter/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { withRouter } from 'react-router';
import {
  ProfileTabs,
  type ProfileTabType,
} from 'app/common/react/components/profile/ProfileHeader/ProfileTabBar';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Flyout, Icon, Text, Touchable } from 'gestalt';

type Props = {
  onClickTab?: Function,
  requestContext: RequestContextType,
  router?: Router,
  storyPinDisplayEnabled: boolean,
  tab: ProfileTabType,
  user: ProfileUser,
  viewer: Viewer,
};

type State = {
  currentTab: string,
  flyoutOpen: boolean,
  hoverMore: boolean,
  tabsVisible: number,
};

type Context = any;

type tabType = {
  id?: string,
  isActive: boolean,
  onClick: () => void,
  tabText: string,
  url: string,
};

const DEFAULT_TABS_VISIBLE = 5;
const PADDING = 160;
const MAX_WIDTH = 800;
const TAB_RECALCULTE_WIDTH = 150;
const DOT_REF_WIDTH = 75;
const RESIZE_THROTTLE_TIMEOUT = 50;

export class BusinessProfileTabBar extends React.Component<Props, State> {
  constructor(props: Props, context: Context) {
    super(props, context);

    this.tabs = this.getTabs();

    this.state = {
      flyoutOpen: false,
      hoverMore: false,
      currentTab: '',
      tabsVisible: DEFAULT_TABS_VISIBLE,
    };
  }

  componentDidMount = () => {
    window.addEventListener('resize', this.calculateTabs);
    this.calculateTabs();
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.calculateTabs);
  };

  setDotRef = (ref: ?HTMLElement) => {
    this.dotRef = ref;
  };

  setTabRef = (ref: ?HTMLElement) => {
    this.tabRef = ref;
  };

  getTabs = () => {
    const { requestContext, storyPinDisplayEnabled, tab, user, viewer } = this.props;

    const isOwnProfile = viewer.isAuth && viewer.username === user.username;
    const isPartner = viewer.isAuth && viewer.isPartner;

    const boardsTab = {
      isActive: tab === ProfileTabs.BOARDS,
      onClick: () => this.handleTabClick(ProfileTabs.BOARDS),
      tabText: i18n._('Boards'),
      id: `${ProfileTabs.BOARDS}-business-profile-tab`,
      url: `/${user.username}/boards/`,
    };

    const pinsTab = {
      isActive: tab === ProfileTabs.PINS,
      onClick: () => this.handleTabClick(ProfileTabs.PINS),
      tabText: i18n._('Pins'),
      id: `${ProfileTabs.PINS}-business-profile-tab`,
      url: `/${user.username}/pins/`,
    };

    const shopTab = {
      isActive: tab === ProfileTabs.SHOP,
      onClick: () => this.handleTabClick(ProfileTabs.SHOP),
      tabText: i18n._('Shop', "Tab to switch to user's shop feed"),
      url: `/${user.username}/_shop/`,
    };

    const videoPinsTab = {
      isActive: tab === ProfileTabs.VIDEO_PINS,
      onClick: () => this.handleTabClick(ProfileTabs.VIDEO_PINS),
      tabText: i18n._('Videos'),
      url: `/${user.username}/video_pins/`,
    };

    const overviewTab = {
      isActive: tab === ProfileTabs.OVERVIEW,
      onClick: () => this.handleTabClick(ProfileTabs.OVERVIEW),
      tabText: i18n._('Overview', 'Overview tab on profile page'),
      url: `/${user.username}/overview/`,
    };

    //TODO: Add these pages
    const activitiesTab = {
      isActive: tab === ProfileTabs.ACTIVITIES,
      onClick: () => this.handleTabClick(ProfileTabs.ACTIVITIES),
      tabText: i18n._('Activity', 'Activities tab on profile page which shows domain pins'),
      url: `/${user.username}/_activities/`,
    };

    const followersTab = {
      isActive: tab === ProfileTabs.FOLLOWERS,
      onClick: () => this.handleTabClick(ProfileTabs.FOLLOWERS),
      tabText: i18n._(
        'Followers',
        'Followers tab on profile page which shows people following you',
      ),
      url: `/${user.username}/_followers/`,
    };

    const followingTab = {
      isActive: tab === ProfileTabs.FOLLOWING,
      onClick: () => this.handleTabClick(ProfileTabs.FOLLOWING),
      tabText: i18n._(
        'Following',
        'Following tab on profile page which shows people following you',
      ),
      url: `/${user.username}/_following/`,
    };

    const storyPinsTab = {
      isActive: tab === ProfileTabs.STORY_PINS,
      onClick: () => this.handleTabClick(ProfileTabs.STORY_PINS),
      tabText: i18n._(
        'Story Pins',
        'Story Pins tab on profile page which shows Story Pins that the user has created',
      ),
      url: `/${user.username}/story_pins/`,
    };

    const tabs = [];

    const hasShopTab =
      user.has_catalog &&
      ((viewer.isAuth && viewer.username === user.username) ||
        new SingleTreatmentExp(requestContext, 'bizpro_web_tur_bazzar').enabled());

    tabs.push(overviewTab);

    if (hasShopTab) {
      tabs.push(shopTab);
    }
    tabs.push(boardsTab, pinsTab);

    if (user.video_pin_count > 0) {
      tabs.push(videoPinsTab);
    }

    if (user.story_pin_count > 0 && storyPinDisplayEnabled) {
      tabs.push(storyPinsTab);
    }

    if (!user.blocked_by_me && (user.pins_done_count || isOwnProfile)) {
      tabs.push({
        isActive: tab === ProfileTabs.TRIED,
        onClick: () => this.handleTabClick(ProfileTabs.TRIED),
        tabText: i18n._('Tries', 'label for tries tab on profile'),
        url: `/${user.username}/tried/`,
      });
    }

    if (user.show_discovered_feed) {
      tabs.push(activitiesTab);
    }
    tabs.push(followersTab, followingTab);

    if (isOwnProfile && !isPartner && !viewer.isLimitedLogin) {
      tabs.push({
        isActive: tab === ProfileTabs.TOPICS,
        onClick: () => this.handleTabClick(ProfileTabs.TOPICS),
        tabText: i18n._('Topics', 'label for topics tab on profile'),
        url: `/${user.username}/topics/`,
      });
    }
    return tabs;
  };

  calculateTabs = throttle(() => {
    const dotrefWidth = (this.dotRef && this.dotRef.offsetWidth) || DOT_REF_WIDTH;
    const width = (this.tabRef && this.tabRef.offsetWidth) + dotrefWidth;
    const maxTabCount = this.tabs.length;

    if (
      (width + PADDING * 2 > window.innerWidth || width > MAX_WIDTH) &&
      this.state.tabsVisible > 0
    ) {
      this.setState({ tabsVisible: this.state.tabsVisible - 1 });
      // Have to recalculate this clientside after each tab is removed until steady-state is reached
      setTimeout(this.calculateTabs, 50);
    } else if (
      width + PADDING * 2 + TAB_RECALCULTE_WIDTH < window.innerWidth &&
      width + TAB_RECALCULTE_WIDTH < MAX_WIDTH &&
      this.state.tabsVisible < maxTabCount
    ) {
      this.setState({ tabsVisible: this.state.tabsVisible + 1 });
      setTimeout(this.calculateTabs, 50);
    }
  }, RESIZE_THROTTLE_TIMEOUT);

  dotRef: ?HTMLElement;
  tabRef: ?HTMLElement;
  tabs: Array<Object>;

  handleOpen = () => {
    this.setState({ flyoutOpen: true });
  };

  handleDismiss = () => {
    this.setState({ flyoutOpen: false });
  };

  hoverMore = () => {
    this.setState({ hoverMore: true });
  };

  unhoverMore = () => {
    this.setState({ hoverMore: false });
  };

  handleTabClick = (tabComponent: ProfileTabType) => {
    const { onClickTab } = this.props;
    let currentTab = null;
    if (this.state && this.state.currentTab) {
      currentTab = this.state.currentTab;
    }
    if (onClickTab) {
      onClickTab(tabComponent);
    }
    if (tabComponent === currentTab) {
      return;
    } else {
      this.setState({ currentTab: tabComponent });
    }

    let element = null;
    switch (tabComponent) {
      case ProfileTabs.OVERVIEW:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_OVERVIEW_TAB;
        break;
      case ProfileTabs.BOARDS:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_BOARDS_TAB;
        break;
      case ProfileTabs.PINS:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_PINS_TAB;
        break;
      case ProfileTabs.SHOP:
        // TODO (pjakubowski) Add PCONST.thrift.ElementType.PARTNER_PROFILE_SHOP_TAB
        element = null;
        break;
      case ProfileTabs.VIDEO_PINS:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_VIDEO_PINS_TAB;
        break;
      case ProfileTabs.FOLLOWING:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_FOLLOWING_TAB;
        break;
      case ProfileTabs.FOLLOWERS:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_FOLLOWERS_TAB;
        break;
      case ProfileTabs.TRIED:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_DID_IT_TAB;
        break;
      case ProfileTabs.STORY_PINS:
        element = PCONST.thrift.ElementType.PARTNER_PROFILE_STORY_PINS_TAB;
        break;
      default:
        element = null;
    }
    if (element) {
      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.PARTNER_PROFILE,
        element,
      });
    }
  };

  renderTab = (tab: tabType, activeTabIndex: number, router?: Router) => (
    <div
      className={cx('BusinessProfileTabItem', {
        Selected: tab.isActive,
      })}
      data-test-id={tab.id}
      key={`tab_${tab.tabText}`}
    >
      <Touchable
        onTouch={() => {
          if (tab.onClick) {
            tab.onClick();
          }
          if (router) {
            router.push(tab.url);
          }
        }}
        shape="pill"
      >
        <Box shape="pill" paddingX={4} paddingY={3}>
          <Text size="md" bold color={tab.isActive ? 'darkGray' : 'gray'} truncate>
            {tab.tabText}
          </Text>
        </Box>
      </Touchable>
    </div>
  );

  renderDropdownTab = (tab: tabType, activeTabIndex: number, router?: Router) => (
    <div
      key={`tab_${tab.tabText}`}
      className={cx('BusinessProfileTabLinkItem', {
        Selected: tab.isActive,
      })}
    >
      <Touchable
        onTouch={() => {
          if (tab.onClick) {
            tab.onClick();
          }
          if (router) {
            router.push(tab.url);
          }
        }}
        shape="square"
      >
        <Box paddingX={4} paddingY={3}>
          <Text align="center" size="md" bold color={tab.isActive ? 'darkGray' : 'gray'}>
            {tab.tabText}
          </Text>
        </Box>
      </Touchable>
    </div>
  );

  renderDot = (dropdownTabs: Array<tabType>, activeTabIndex: number, router?: Router) => (
    <Box flex="shrink">
      <div ref={this.setDotRef} className={cx('BusinessProfileTabMoreItem')}>
        <Touchable
          onMouseEnter={() => {
            this.setState({ hoverMore: true });
          }}
          onMouseLeave={() => {
            this.setState({ hoverMore: false });
          }}
          onTouch={this.state.flyoutOpen ? this.handleDismiss : this.handleOpen}
          shape="pill"
        >
          <Box display="flex" shape="pill" paddingX={4} paddingY={3}>
            <Box>
              <Text bold color="gray" inline>
                {i18n._('More', 'Show more menu items')}
              </Text>
            </Box>
            <Box alignSelf="center" paddingX={2}>
              <Icon
                accessibilityLabel={i18n._('More', 'Show more menu items')}
                icon={this.state.flyoutOpen ? 'arrow-up' : 'arrow-down'}
                inline
                color="gray"
                size={16}
              />
            </Box>
          </Box>
        </Touchable>
        {this.state.flyoutOpen && (
          <Flyout
            anchor={this.dotRef}
            idealDirection="down"
            onDismiss={this.handleDismiss}
            size="md"
          >
            <Box column={12}>
              <Box alignItems="center" display="flex" direction="column" justifyContent="center">
                {dropdownTabs.map((tab) => this.renderDropdownTab(tab, activeTabIndex, router))}
              </Box>
            </Box>
          </Flyout>
        )}
      </div>
    </Box>
  );

  render() {
    const { tab, router, user, viewer } = this.props;
    const isOwnProfile = viewer.isAuth && viewer.username === user.username;
    const hasBoards = user.board_count > 0;

    // Store items to display
    const tabs = this.tabs.slice();
    const dropdownTabs = tabs.splice(this.state.tabsVisible, tabs.length - this.state.tabsVisible);
    // There should always be a tab with isActive set to true, but just in case
    // the find call fails and returns -1 lets default to 0
    const activeTabIndex = Math.max(tabs.findIndex((tab) => tab.isActive), 0);

    return (
      <div className="BusinessProfileTabBar">
        <Box display="flex" direction="row" justifyContent="center" alignItems="center">
          <Box paddingY={2}>
            <div ref={this.setTabRef}>
              <Box display="flex" direction="row" justifyContent="center" alignItems="center">
                {tabs.map((tab) => this.renderTab(tab, activeTabIndex, router))}
              </Box>
            </div>
          </Box>
          {dropdownTabs.length > 0 && this.renderDot(dropdownTabs, activeTabIndex, router)}
          {tab === ProfileTabs.BOARDS &&
            isOwnProfile &&
            hasBoards && (
              <Box paddingY={2}>
                <BoardSortSelectlist
                  isOwnProfile={isOwnProfile}
                  hasCustomOrder={user.has_custom_board_sorting_order}
                />
              </Box>
            )}
        </Box>
      </div>
    );
  }
}

export default compose(
  withViewer,
  withRouter,
  withRequestContext,
)(BusinessProfileTabBar);
